import React, { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "../../lib/api";
import { formatINR, categoryMeta } from "../../lib/format";
import { Card } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import { Button } from "../../components/ui/button";
import { Lightbulb, TrendingUp, TrendingDown, RefreshCw, CheckCircle2, Copy, EyeOff } from "lucide-react";
import { AreaChart, Area, XAxis, Tooltip as RTooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";

const USAGE = [
  { v: "regular", l: "I use this regularly" },
  { v: "occasional", l: "I occasionally use this" },
  { v: "unused", l: "I no longer use this" },
  { v: "unsure", l: "I'm unsure" },
];

function Stat({ label, value, sub, icon: Icon }) {
  return (
    <Card className="rounded-2xl border-border shadow-card-sm p-5">
      <div className="flex items-center gap-2 text-muted-foreground"><Icon size={16} strokeWidth={1.5} /><span className="text-xs">{label}</span></div>
      <p className="font-heading text-2xl font-medium text-ink mt-2 tnum">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </Card>
  );
}

function Section({ title, items, renderItem, empty }) {
  return (
    <Card className="rounded-2xl border-border shadow-card-sm p-5">
      <p className="text-sm font-medium text-ink mb-3">{title}</p>
      {(!items || items.length === 0) ? <p className="text-sm text-muted-foreground py-3">{empty}</p> :
        <div className="space-y-2">{items.map(renderItem)}</div>}
    </Card>
  );
}

export default function Insights() {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ins, tr] = await Promise.all([api.get("/insights"), api.get("/trends")]);
      setData(ins.data); setTrend(tr.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const mark = async (c, flag) => {
    try { await api.patch(`/commitments/${c.id}/usage`, { usage_flag: flag }); toast.success("Thanks — noted."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-40 rounded" /><div className="grid grid-cols-2 gap-4">{[1,2,3,4].map(i=><Skeleton key={i} className="h-28 rounded-2xl" />)}</div></div>;

  const hc = data.highest_category;
  const change = data.change_from_previous;
  const chartData = trend.map((t) => ({ month: t.month?.slice(5), value: t.monthly_recurring }));
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-1">
        <Lightbulb size={22} className="text-navy" strokeWidth={1.5} />
        <h1 className="font-heading text-2xl font-medium text-ink">Insights</h1>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Stat label="Total committed (monthly)" value={formatINR(data.total_committed_monthly)}
          sub={change ? `${change.delta >= 0 ? "+" : "−"}${formatINR(Math.abs(change.delta))} vs last month` : "Monthly equivalent"}
          icon={change && change.delta < 0 ? TrendingDown : TrendingUp} />
        <Stat label="Highest category" value={hc ? categoryMeta(hc.category).label : "—"} sub={hc ? formatINR(hc.amount) : ""} icon={Lightbulb} />
        <Stat label="Reduced via COMMITMENTT" value={formatINR(data.self_reported_savings)} sub="From paused/cancelled (self-reported)" icon={CheckCircle2} />
        <Stat label="Annual renewals ahead" value={data.annual_renewals?.length || 0} sub="Plan for the lump sum" icon={RefreshCw} />
      </div>

      {/* Spending trend */}
      <Card className="rounded-2xl border-border shadow-card-sm p-5" data-testid="trend-card">
        <p className="text-sm font-medium text-ink mb-1">Committed total over time</p>
        {chartData.length < 2 ? (
          <p className="text-xs text-muted-foreground py-6">Tracking began this month. Your month-over-month trend will appear here as the weeks go by.</p>
        ) : (
          <div className="h-40 -ml-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="mint" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <RTooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Area type="monotone" dataKey="value" stroke="#10B981" strokeWidth={2} fill="url(#mint)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Section title="Upcoming annual renewals" items={data.annual_renewals} empty="No annual renewals recorded."
        renderItem={(c) => (
          <div key={c.id} className="flex justify-between items-center rounded-xl bg-secondary/40 px-4 py-2.5">
            <span className="text-sm text-ink">{c.name}</span>
            <span className="text-sm font-medium text-ink tnum">{formatINR(c.amount)}<span className="text-xs text-muted-foreground"> · {formatINR(c.monthly_equivalent)}/mo</span></span>
          </div>
        )} />

      <Section title="EMIs nearing completion" items={data.emis_completing} empty="No EMIs are near completion."
        renderItem={(c) => (
          <div key={c.id} className="flex justify-between items-center rounded-xl bg-secondary/40 px-4 py-2.5">
            <span className="text-sm text-ink">{c.name}</span>
            <span className="text-xs text-slatey">{c.instalments_completed}/{c.total_instalments} paid · {c.remaining_instalments} left</span>
          </div>
        )} />

      <Section title="Potential duplicate subscriptions" items={data.potential_duplicates} empty="No potential duplicates found."
        renderItem={(c) => (
          <div key={c.id} className="flex justify-between items-center rounded-xl bg-calm-pendingbg px-4 py-2.5">
            <span className="text-sm text-ink flex items-center gap-2"><Copy size={14} className="text-calm-pending" />{c.name}</span>
            <span className="text-sm font-medium text-ink tnum">{formatINR(c.amount)}</span>
          </div>
        )} />

      <Section title="Marked as no longer used" items={data.unused} empty="Nothing marked unused."
        renderItem={(c) => (
          <div key={c.id} className="flex justify-between items-center rounded-xl bg-secondary/40 px-4 py-2.5">
            <span className="text-sm text-ink flex items-center gap-2"><EyeOff size={14} className="text-muted-foreground" />{c.name}</span>
            <span className="text-sm font-medium text-ink tnum">{formatINR(c.amount)}</span>
          </div>
        )} />

      <Card className="rounded-2xl border-border shadow-card-sm p-5" data-testid="usage-review">
        <p className="text-sm font-medium text-ink mb-1">Review your flexible commitments</p>
        <p className="text-xs text-muted-foreground mb-4">Tell us how you actually use these. We'll suggest what to review — never automatically cancel.</p>
        {(!data.flexible_for_review || data.flexible_for_review.length === 0) ? (
          <p className="text-sm text-muted-foreground py-3">No flexible commitments to review.</p>
        ) : (
          <div className="space-y-4">
            {data.flexible_for_review.map((c) => (
              <div key={c.id} className="border-b border-border/60 pb-4 last:border-0 last:pb-0">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-ink text-sm">{c.name}</span>
                  <span className="text-sm text-slatey tnum">{formatINR(c.amount)}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {USAGE.map((u) => (
                    <Button key={u.v} size="sm" variant={c.usage_flag === u.v ? "default" : "secondary"}
                      onClick={() => mark(c, u.v)}
                      className={`rounded-full text-xs h-8 ${c.usage_flag === u.v ? "bg-navy text-white" : "bg-secondary text-slatey"}`}
                      data-testid={`usage-${c.id}-${u.v}`}>
                      {u.l}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
